import {
  createSlice,
  createAsyncThunk,
  type PayloadAction,
} from "@reduxjs/toolkit";
import { getMentorBookmarks as getMentorBookmarksApi } from "../../api";
import type { MentorBookmark } from "../../shared/bookmarkTypes";

export const hydrateMentorBookmarks = createAsyncThunk<
  MentorBookmark[],
  string,
  { rejectValue: string }
>("mentorBookmarks/hydrateMentorBookmarks", async (userId, thunkApi) => {
  try {
    return await getMentorBookmarksApi(userId);
  } catch (error) {
    return thunkApi.rejectWithValue(
      error instanceof Error
        ? error.message
        : "Failed to hydrate mentor bookmarks",
    );
  }
});

interface MentorBookmarksState {
  bookmarks: MentorBookmark[];
  status: "idle" | "pending" | "succeeded" | "failed";
  error: string | null;
}

const initialState: MentorBookmarksState = {
  bookmarks: [],
  status: "idle",
  error: null,
};

const mentorBookmarksSlice = createSlice({
  name: "mentorBookmarks",
  initialState,
  reducers: {
    toggleMentorBookmark: (state, action: PayloadAction<MentorBookmark>) => {
      const { userId, mentorId } = action.payload;
      const alreadyBookmarked = state.bookmarks.some(
        (bookmark) => bookmark.mentorId === mentorId,
      );

      if (alreadyBookmarked) {
        state.bookmarks = state.bookmarks.filter(
          (bookmark) => bookmark.mentorId !== mentorId,
        );
        return;
      } else {
        state.bookmarks.push({ userId, mentorId });
      }
    },

    clearMentorBookmarks: (state) => {
      state.status = "idle";
      state.bookmarks = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(hydrateMentorBookmarks.pending, (state) => {
        state.status = "pending";
        state.error = null;
      })
      .addCase(hydrateMentorBookmarks.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = null;
        state.bookmarks = action.payload;
      })
      .addCase(hydrateMentorBookmarks.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ??
          action.error.message ??
          "Failed to hydrate bookmarks";
      });
  },
});

export const { toggleMentorBookmark, clearMentorBookmarks } =
  mentorBookmarksSlice.actions;

export default mentorBookmarksSlice.reducer;
